import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, tap } from 'rxjs';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MatDialogRef } from '@angular/material/dialog';
import { AdvertiserGroupCreationDialogComponent } from '../../modals/advertiser-group-creation-dialog/advertiser-group-creation-dialog.component';
import { Tag, TagType } from '~core/models/tag.models';
import { TagService } from '~app/app/~shared/services/tag.service';

@Component({
  selector: 'advertiser-group-creation-form',
  templateUrl: './advertiser-group-creation-form.component.html',
  styleUrl: './advertiser-group-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class AdvertiserGroupCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _tagService = inject(TagService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(
    MatDialogRef<AdvertiserGroupCreationDialogComponent>
  );

  advertiser = input<Tag>();

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  sectors$ = this._tagService.list({
    type: TagType.SECTOR
  });

  isSubmitting = signal(false);

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    })
  });

  ngOnInit(): void {
    if (this.advertiser()) {
      this.form.patchValue({
        name: this.advertiser()?.name
      });
    }
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const groupId = this.advertiser()?.id;

    if (groupId) {
      this.updateAdvertiserGroup(groupId);
    } else {
      this.createAdvertiserGroup();
    }
  }

  createAdvertiserGroup(): void {
    const formValue = this.form.value;

    this._tagService
      .create({
        name: formValue.name as string,
        type: TagType.GROUP
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Groupe créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateAdvertiserGroup(groupId: string): void {
    const formValue = this.form.value;

    this._tagService
      .update(groupId, {
        name: formValue.name as string,
        type: TagType.GROUP
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Groupe modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }
}
